import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Lightning, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const RegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (formData.password.length < 6) {
      toast.error("Şifre en az 6 karakter olmalıdır");
      setLoading(false);
      return;
    }

    const result = await register({
      ...formData,
      role: "tenant", // Only tenants can self-register
    });

    if (result.success) {
      toast.success("Kayıt başarılı!");
      navigate("/tenant");
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex flex-1 bg-indigo-600 items-center justify-center p-12">
        <div className="max-w-lg text-white">
          <img
            src="https://images.unsplash.com/photo-1758523668950-867adc79e79a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHRlbmFudCUyMHVzaW5nJTIwcGhvbmUlMjBpbiUyMG1vZGVybiUyMGFwYXJ0bWVudHxlbnwwfHx8fDE3NzM0NzY2MDd8MA&ixlib=rb-4.1.0&q=85"
            alt="User"
            className="w-full rounded-2xl shadow-2xl mb-8"
          />
          <h2 className="text-3xl font-heading font-bold mb-4">
            {t("auth.register_title")}
          </h2>
          <p className="text-indigo-100 text-lg leading-relaxed">
            Kayıt olun ve enerji tüketiminizi takip edin. Bakiye yükleyin, tüketim raporlarınızı görüntüleyin.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
              <Lightning size={28} weight="duotone" className="text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">
                Enerji Kontrol Hub
              </h1>
              <p className="text-sm text-slate-500">Akıllı Sayaç Yönetimi</p>
            </div>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-heading">{t("auth.register_title")}</CardTitle>
              <CardDescription>
                {t("auth.register_subtitle")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("common.name")}</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Ahmet Yılmaz"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    data-testid="register-name-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t("common.email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="ornek@email.com"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    data-testid="register-email-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t("common.phone")} ({t("common.optional")})</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="0532 123 4567"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    data-testid="register-phone-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t("common.password")}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      required
                      data-testid="register-password-input"
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeSlash size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">En az 6 karakter</p>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? t("auth.registering") : t("auth.register_title")}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm">
                <span className="text-slate-500">{t("auth.have_account")} </span>
                <Link
                  to="/login"
                  className="text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
                  data-testid="login-link"
                >
                  {t("auth.login")}
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
